import { AxiosRequest } from "_services/api";
import { toastErro } from "components/Toast";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DirecaoOrdenacao,
  IOrdenacao,
} from "../../SolicitacaoPlaca/interfaces/IOrdenacao";
import { mensagemDeErro } from "../../SolicitacaoPlaca/utils/mensagemDeErro";
import {
  FILTROS_SOLICITACAO_TERCEIROS_VAZIOS,
  ORDENACAO_SOLICITACAO_TERCEIROS_PADRAO,
} from "../constants/filtrosSolicitacaoTerceiros";
import { IFiltrosSolicitacaoTerceiros } from "../interfaces/IFiltrosSolicitacaoTerceiros";
import { IDadosEnvioTerceiros } from "../interfaces/IFornecedorTerceiros";
import { ISolicitacaoTerceiros } from "../interfaces/ISolicitacaoTerceiros";
import { exportarSolicitacoesTerceirosXlsx } from "../utils/exportarSolicitacoesTerceirosXlsx";
import { montarParametrosSolicitacoesTerceiros } from "../utils/montarParametrosSolicitacoesTerceiros";

const LIMITE_PADRAO = 50;
const LIMITE_TODAS_AS_PAGINAS = -1;
const STATUS_CONFLITO = 409;

export type ResultadoEnvioTerceiros =
  | { ok: true; idLote: number }
  | { ok: false; conflito: boolean };

export function useSolicitacoesTerceiros() {
  // Uma instância só: se o makeApi entrar nas dependências do efeito a cada render, a busca entra em loop.
  const msAw = useMemo(
    () => AxiosRequest.makeApi(process.env.REACT_APP_MS_AW),
    []
  );
  const [filtros, setFiltros] = useState<IFiltrosSolicitacaoTerceiros>(
    FILTROS_SOLICITACAO_TERCEIROS_VAZIOS
  );
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(LIMITE_PADRAO);
  const [ordenacao, setOrdenacao] = useState<IOrdenacao>(
    ORDENACAO_SOLICITACAO_TERCEIROS_PADRAO
  );
  const [versao, setVersao] = useState(0);
  const [reiniciarPaginacao, setReiniciarPaginacao] = useState(false);
  const [solicitacoes, setSolicitacoes] = useState<ISolicitacaoTerceiros[]>([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const ultimaBusca = useRef(0);

  useEffect(() => {
    const busca = ++ultimaBusca.current;

    async function buscar() {
      setCarregando(true);
      try {
        const parametros = montarParametrosSolicitacoesTerceiros({
          filtros,
          pagina,
          limite,
          ordenacao,
        });
        const { data } = await msAw.get(`/solicitacao-placa?${parametros}`);
        if (busca !== ultimaBusca.current) return;
        setSolicitacoes(data?.itens ?? []);
        setTotalRegistros(data?.meta?.total ?? 0);
      } catch (erro) {
        if (busca !== ultimaBusca.current) return;
        setSolicitacoes([]);
        setTotalRegistros(0);
        toastErro(mensagemDeErro(erro, "Erro ao buscar as solicitações"));
      } finally {
        if (busca === ultimaBusca.current) setCarregando(false);
      }
    }

    buscar();
  }, [msAw, filtros, pagina, limite, ordenacao, versao]);

  const voltarParaPrimeiraPagina = useCallback(() => {
    setPagina(1);
    setReiniciarPaginacao((valor) => !valor);
  }, []);

  const aplicarFiltros = useCallback(
    (novosFiltros: IFiltrosSolicitacaoTerceiros) => {
      setFiltros(novosFiltros);
      setVersao((valor) => valor + 1);
      voltarParaPrimeiraPagina();
    },
    [voltarParaPrimeiraPagina]
  );

  const mudarLimite = useCallback(
    (novoLimite: number) => {
      setLimite(novoLimite);
      voltarParaPrimeiraPagina();
    },
    [voltarParaPrimeiraPagina]
  );

  // A tabela já volta sozinha para a página 1 quando a ordenação muda.
  const ordenar = useCallback((campo: string, direcao: DirecaoOrdenacao) => {
    setOrdenacao({ campo, direcao });
    setPagina(1);
  }, []);

  const recarregar = useCallback(() => setVersao((valor) => valor + 1), []);

  const exportar = useCallback(async () => {
    setExportando(true);
    try {
      const parametros = montarParametrosSolicitacoesTerceiros({
        filtros,
        pagina: 1,
        limite: LIMITE_TODAS_AS_PAGINAS,
        ordenacao,
      });
      const { data } = await msAw.get(`/solicitacao-placa?${parametros}`);
      exportarSolicitacoesTerceirosXlsx(data?.itens ?? []);
    } catch (erro) {
      toastErro(mensagemDeErro(erro, "Erro ao exportar as solicitações"));
    } finally {
      setExportando(false);
    }
  }, [msAw, filtros, ordenacao]);

  const enviar = useCallback(
    async (
      idsSolicitacao: number[],
      dados: IDadosEnvioTerceiros
    ): Promise<ResultadoEnvioTerceiros> => {
      setEnviando(true);
      try {
        const { data } = await msAw.post("/solicitacao-placa/lote/terceiros", {
          idsSolicitacao,
          ...dados,
        });
        recarregar();
        return { ok: true, idLote: data.idLote };
      } catch (erro: any) {
        toastErro(mensagemDeErro(erro, "Erro ao enviar a solicitação"));
        const conflito = erro?.response?.status === STATUS_CONFLITO;
        if (conflito) recarregar();
        return { ok: false, conflito };
      } finally {
        setEnviando(false);
      }
    },
    [msAw, recarregar]
  );

  return {
    solicitacoes,
    totalRegistros,
    carregando,
    limite,
    reiniciarPaginacao,
    aplicarFiltros,
    setPagina,
    mudarLimite,
    ordenar,
    recarregar,
    exportar,
    exportando,
    enviar,
    enviando,
  };
}
